import { format } from 'date-fns';
import { AnimatePresence, motion } from 'framer-motion';
import { Bot, Check, ChevronDown, ChevronRight, ClipboardEdit, Clock, Code, ExternalLink, Headphones, Mail, Phone, PhoneCall, UserCheck, Wrench, X } from 'lucide-react';
import React from 'react';

import { InspectArtifactButton } from '@/components/artifact-inspector/InspectArtifactButton';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { ArtifactType } from '@/services/types/artifact-digest-types';

/** Step trace from AgentFlow execution */
export interface StepTrace {
  step_id: string | number;
  step_type: string;
  workflow_id: string;
  status: 'running' | 'completed' | 'failed' | 'waiting';
  started_at?: string;
  completed_at?: string;
  duration_ms?: number;
  input?: Record<string, unknown>;
  output?: Record<string, unknown>;
  error_message?: string;
  wait_until?: string;
  wait_label?: string;
  review_approval_id?: string;
  children?: StepTrace[];
}

interface SchemaProperty {
  type?: string;
  description?: string;
  enum?: string[];
  default?: unknown;
}

export interface CrashingOutPendingReview {
  approval_id: string;
  response_schema?: Record<string, unknown> | null;
  form_title?: string | null;
  form_description?: string | null;
  created_at?: string | null;
  source_workflow_id?: string | null;
  source_run_id?: string | null;
}

export interface CrashingOutExecutionHistoryProps {
  /** Step traces from workflow execution */
  traces: StepTrace[];
  /** Selected run status (e.g. running, failed, completed) */
  runStatus?: string;
  /** Matter ID for artifact links */
  matterId?: string;
  /** Max number of items to show initially */
  maxItems?: number;
  /** Callback when user wants to make a dialpad call */
  onMakeCall?: (phoneNumber?: string) => void;
  /** Callback when user wants to enter BPO results */
  onEnterResults?: (stepId: string | number) => void;
  /** Pending human reviews for the current coverage/run */
  pendingReviews?: CrashingOutPendingReview[];
  /** Form values keyed by approval_id */
  reviewFormDataByApproval?: Record<string, Record<string, unknown>>;
  /** Current approval being submitted */
  submittingReviewId?: string | null;
  /** Update a form field value for an approval */
  onReviewFieldChange?: (
    approvalId: string,
    field: string,
    value: unknown,
  ) => void;
  /** Submit a pending review */
  onSubmitReview?: (approvalId: string) => Promise<void> | void;
  /** Auto-expand the last LLM block and show all steps */
  defaultExpandLast?: boolean;
  /** Auto-scroll timeline to the latest visible step */
  autoScrollToLatest?: boolean;
  /** When review is pending, show the review form first and collapse the rest */
  focusPendingReview?: boolean;
  /** Use the staging-style Temporal presentation */
  legacyTemporalStyle?: boolean;
}

/** Check if step type is a dialpad/BPO call step */
const isDialpadStep = (stepType: string): boolean => {
  const lowerType = stepType.toLowerCase();
  return (
    lowerType.includes('dialpad') ||
    lowerType.includes('bpo') ||
    (lowerType.includes('call') && !lowerType.includes('bland'))
  );
};

const getStepIcon = (stepType: string) => {
  const lowerType = stepType.toLowerCase();
  if (lowerType.includes('llm')) {
    return <Bot className="h-3 w-3" />;
  }
  if (lowerType.includes('email')) {
    return <Mail className="h-3 w-3" />;
  }
  if (lowerType.includes('call') || lowerType.includes('bland') || lowerType.includes('dialpad') || lowerType.includes('bpo')) {
    return <Phone className="h-3 w-3" />;
  }
  if (lowerType.includes('wait')) {
    return <Clock className="h-3 w-3" />;
  }
  return <Clock className="h-3 w-3" />;
};

const getStepTypeLabel = (stepType: string): string => {
  switch (stepType) {
    case 'BlandBlock':
      return 'Voice AI Call';
    case 'DialpadCallBlock':
      return 'RingCentral Call';
    case 'BPOBlock':
      return 'BPO Call';
    case 'SendEmailBlock':
      return 'Email';
    case 'SendTaskNotificationBlock':
      return 'Notification';
    case 'LLMBlock':
      return 'LLM Driver';
    case 'Wait':
      return 'Wait';
    default:
      return stepType.replace(/Block$/, '').replace(/([A-Z])/g, ' $1').trim();
  }
};

const formatDuration = (ms: number): string => {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${Math.round(ms / 1000)}s`;
  return `${Math.round(ms / 60000)}m`;
};

const formatDate = (dateStr: string | undefined): string => {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const safeStringify = (value: unknown, spacing = 2): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;

  const seen = new WeakSet<object>();

  try {
    return JSON.stringify(
      value,
      (_key, currentValue) => {
        if (typeof currentValue === 'bigint') {
          return currentValue.toString();
        }
        if (typeof currentValue === 'object' && currentValue !== null) {
          if (seen.has(currentValue)) {
            return '[Circular]';
          }
          seen.add(currentValue);
        }
        return currentValue;
      },
      spacing,
    ) ?? String(value);
  } catch {
    return String(value);
  }
};

const previewValue = (value: unknown, maxLength = 500): string => {
  if (value === null || value === undefined) return '';
  const text = safeStringify(value);
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}...`;
};

const toRecord = (value: unknown): Record<string, unknown> => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return value as Record<string, unknown>;
};

const toRecordArray = (value: unknown): Record<string, unknown>[] => {
  if (!Array.isArray(value)) return [];
  return value
    .filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === 'object' && !Array.isArray(item));
};

// ============================================================================
// Helper to format phone numbers
// ============================================================================

const formatPhoneNumber = (phone: string | undefined): string => {
  if (!phone) return '—';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return phone;
};

// ============================================================================
// Detail Row - Reusable component for displaying key-value pairs
// ============================================================================

const DetailRow: React.FC<{
  label: string;
  value: React.ReactNode;
  className?: string;
}> = ({ label, value, className }) => (
  <div className={cn('flex items-start gap-2', className)}>
    <span className="text-[10px] font-medium text-zinc-500 min-w-[80px] shrink-0">{label}:</span>
    <span className="text-[11px] text-zinc-800">{value || '—'}</span>
  </div>
);

// ============================================================================
// Evaluation Table - Reusable table for evaluation results
// ============================================================================

const formatEvalValue = (value: unknown): React.ReactNode => {
  if (typeof value === 'boolean') {
    return (
      <span className={cn(
        'px-1.5 py-0.5 rounded text-[10px] font-medium',
        value ? 'bg-emerald-100 text-emerald-700' : 'bg-zinc-100 text-zinc-600',
      )}>
        {value ? 'Yes' : 'No'}
      </span>
    );
  }
  if (value === null || value === undefined) {
    return <span className="text-zinc-400">—</span>;
  }
  if (typeof value === 'object') {
    return <span className="font-mono text-[10px]">{safeStringify(value, 0)}</span>;
  }
  return String(value);
};

const formatEvalKey = (key: string): string => {
  return key
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
};

const EvaluationTable: React.FC<{
  evaluation: Record<string, unknown>;
  title?: string;
  colorScheme?: 'violet' | 'blue';
}> = ({ evaluation, title = 'Evaluation', colorScheme = 'violet' }) => {
  if (!evaluation || Object.keys(evaluation).length === 0) return null;

  const colors = colorScheme === 'violet'
    ? { bg: 'bg-violet-50', border: 'border-violet-100', headerBg: 'bg-violet-100', headerText: 'text-violet-700', cellText: 'text-violet-900' }
    : { bg: 'bg-blue-50', border: 'border-blue-100', headerBg: 'bg-blue-100', headerText: 'text-blue-700', cellText: 'text-blue-900' };

  return (
    <div className="border-t border-zinc-100 pt-2">
      <div className="text-[10px] font-medium text-zinc-500 mb-1.5">{title}</div>
      <div className={cn('rounded border overflow-hidden', colors.border)}>
        <table className="w-full text-[11px]">
          <tbody>
            {Object.entries(evaluation).map(([key, value], index) => (
              <tr
                key={key}
                className={cn(
                  index % 2 === 0 ? colors.bg : 'bg-white',
                )}
              >
                <td className={cn('px-2 py-1.5 font-medium', colors.headerText, 'w-[140px]')}>
                  {formatEvalKey(key)}
                </td>
                <td className={cn('px-2 py-1.5', colors.cellText)}>
                  {formatEvalValue(value)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============================================================================
// Voice AI Call Details (BlandBlock)
// ============================================================================

// Helper to parse a value that might be a JSON string
const parseIfJsonString = (value: unknown): unknown => {
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }
  return value;
};

// Helper to extract and flatten the evaluation/result from output
// Handles: output.evaluation.result (where result is a JSON string)
const extractEvaluation = (output: Record<string, unknown> | undefined): Record<string, unknown> | undefined => {
  if (!output) return undefined;

  // Check for output.evaluation.result (the actual structure from the API)
  const evaluation = output.evaluation as Record<string, unknown> | undefined;
  if (evaluation) {
    const {result} = evaluation;
    if (result) {
      // result is often a JSON string that needs to be parsed
      const parsed = parseIfJsonString(result);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    }
    // If no result key, return evaluation itself
    return evaluation;
  }

  return undefined;
};

const VoiceAICallDetails: React.FC<{
  input: Record<string, unknown>;
  output: Record<string, unknown>;
  startedAt?: string;
  matterId?: string;
}> = ({ input, output, startedAt, matterId }) => {
  const phoneNumber = (input.phone_number || output.phone_number) as string | undefined;
  const audioConversationId = output.audio_conversation_id as string | undefined;
  const callStatus = output.call_status as string | undefined;

  // Extract evaluation from output (handles nested Result wrappers)
  const evaluation = React.useMemo(() => extractEvaluation(output), [output]);

  // Get summary from evaluation or output
  const summary = (evaluation?.summary || evaluation?.call_summary || output.summary) as string | undefined;

  return (
    <div className="space-y-3">
      {/* Call Info */}
      <div className="space-y-1.5">
        <DetailRow label="Phone" value={formatPhoneNumber(phoneNumber)} />
        <DetailRow label="Called" value={startedAt ? formatDate(startedAt) : '—'} />
        <DetailRow
          label="Status"
          value={
            <span className={cn(
              'px-1.5 py-0.5 rounded text-[10px] font-medium',
              callStatus === 'completed' && 'bg-emerald-100 text-emerald-700',
              callStatus === 'failed' && 'bg-red-100 text-red-700',
              callStatus === 'voicemail' && 'bg-amber-100 text-amber-700',
              !['completed', 'failed', 'voicemail'].includes(callStatus || '') && 'bg-zinc-100 text-zinc-700',
            )}>
              {callStatus || 'Unknown'}
            </span>
          }
        />
        {audioConversationId && matterId && (
          <DetailRow
            label="Recording"
            value={
              <InspectArtifactButton
                artifactId={audioConversationId}
                artifactType={ArtifactType.AUDIO_CONVERSATION}
                matterId={matterId}
                compact
                icon={<Headphones className="h-3 w-3" />}
              />
            }
          />
        )}
      </div>

      {/* Summary */}
      {summary && (
        <div className="border-t border-zinc-100 pt-2">
          <div className="text-[10px] font-medium text-zinc-500 mb-1">Call Summary</div>
          <p className="text-[11px] text-zinc-700 leading-relaxed bg-zinc-50 rounded p-2">
            {summary}
          </p>
        </div>
      )}

      {/* Evaluation */}
      <EvaluationTable evaluation={evaluation || {}} colorScheme="violet" />
    </div>
  );
};

// ============================================================================
// Email Details (SendEmailBlock)
// ============================================================================

const EmailDetails: React.FC<{
  input: Record<string, unknown>;
  output: Record<string, unknown>;
  startedAt?: string;
}> = ({ input, output, startedAt }) => {
  const toRecipients = (input.to as string[]) || [];
  const subject = input.subject as string | undefined;
  const body = input.body as string | undefined;
  const conversationUrl = output.conversation_url as string | undefined;

  // Strip HTML tags for preview
  const bodyPreview = body
    ? body.replace(/<[^>]*>/g, '').slice(0, 200) + (body.length > 200 ? '...' : '')
    : undefined;

  return (
    <div className="space-y-2">
      <div className="space-y-1.5">
        <DetailRow label="To" value={toRecipients.join(', ') || '—'} />
        <DetailRow label="Subject" value={subject} />
        <DetailRow label="Sent" value={startedAt ? formatDate(startedAt) : '—'} />
        {conversationUrl && (
          <DetailRow
            label="View"
            value={
              <a
                href={conversationUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-violet-600 hover:text-violet-700 flex items-center gap-1"
              >
                Open in Front <ExternalLink className="h-3 w-3" />
              </a>
            }
          />
        )}
      </div>

      {/* Email Preview */}
      {bodyPreview && (
        <div className="border-t border-zinc-100 pt-2">
          <div className="text-[10px] font-medium text-zinc-500 mb-1">Preview</div>
          <p className="text-[11px] text-zinc-600 leading-relaxed bg-zinc-50 rounded p-2">
            {bodyPreview}
          </p>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Notification Details (SendTaskNotificationBlock)
// ============================================================================

const NotificationDetails: React.FC<{
  input: Record<string, unknown>;
  startedAt?: string;
}> = ({ input, startedAt }) => {
  const title = input.title as string | undefined;
  const description = input.description as string | undefined;
  const category = input.category as string | undefined;

  return (
    <div className="space-y-2">
      <div className="space-y-1.5">
        <DetailRow label="Title" value={title} />
        <DetailRow label="Sent" value={startedAt ? formatDate(startedAt) : '—'} />
        {category && (
          <DetailRow
            label="Type"
            value={
              <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700">
                {category}
              </span>
            }
          />
        )}
      </div>

      {/* Description */}
      {description && (
        <div className="border-t border-zinc-100 pt-2">
          <div className="text-[10px] font-medium text-zinc-500 mb-1">Content</div>
          <p className="text-[11px] text-zinc-700 leading-relaxed bg-zinc-50 rounded p-2">
            {description}
          </p>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Task Update Details (SendTaskUpdateBlock) - Adds comment/activity to task
// ============================================================================

const TaskCommentDetails: React.FC<{
  input: Record<string, unknown>;
  startedAt?: string;
}> = ({ input, startedAt }) => {
  const update = input.update as string | undefined;
  const updateType = input.update_type as string | undefined;
  const agent = input.agent as string | undefined;

  return (
    <div className="space-y-2">
      <div className="space-y-1.5">
        <DetailRow label="Time" value={startedAt ? formatDate(startedAt) : '—'} />
        {updateType && (
          <DetailRow
            label="Type"
            value={
              <span className={cn(
                'px-1.5 py-0.5 rounded text-[10px] font-medium',
                updateType === 'ACTIVITY' ? 'bg-violet-100 text-violet-700' : 'bg-zinc-100 text-zinc-700',
              )}>
                {updateType}
              </span>
            }
          />
        )}
        {agent && <DetailRow label="By" value={agent.replace(/_/g, ' ')} />}
      </div>

      {/* Update Content */}
      {update && (
        <div className="border-t border-zinc-100 pt-2">
          <div className="text-[10px] font-medium text-zinc-500 mb-1">Comment</div>
          <p className="text-[11px] text-zinc-700 leading-relaxed bg-amber-50 border border-amber-100 rounded p-2">
            {update}
          </p>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Task Field Update Details (UpdateTaskBlock) - Updates task fields like due_date
// ============================================================================

const TaskFieldUpdateDetails: React.FC<{
  input: Record<string, unknown>;
  startedAt?: string;
}> = ({ input, startedAt }) => {
  // Extract the fields that were updated (non-null values except task_id and matter_id)
  const updatedFields: Record<string, unknown> = {};
  const skipFields = ['task_id', 'matter_id', 'success'];

  Object.entries(input).forEach(([key, value]) => {
    if (!skipFields.includes(key) && value !== null && value !== undefined) {
      updatedFields[key] = value;
    }
  });

  // Format specific field values for display
  const formatFieldValue = (key: string, value: unknown): React.ReactNode => {
    if (key === 'due_date' || key === 'sla_resolution_target') {
      const dateVal = value as string;
      try {
        return formatDate(dateVal);
      } catch {
        return String(value);
      }
    }
    if (typeof value === 'boolean') {
      return (
        <span className={cn(
          'px-1.5 py-0.5 rounded text-[10px] font-medium',
          value ? 'bg-emerald-100 text-emerald-700' : 'bg-zinc-100 text-zinc-600',
        )}>
          {value ? 'Yes' : 'No'}
        </span>
      );
    }
    return String(value);
  };

  return (
    <div className="space-y-2">
      <DetailRow label="Time" value={startedAt ? formatDate(startedAt) : '—'} />

      {/* Updated Fields Table */}
      {Object.keys(updatedFields).length > 0 && (
        <div className="border-t border-zinc-100 pt-2">
          <div className="text-[10px] font-medium text-zinc-500 mb-1.5">Fields Updated</div>
          <div className="rounded border border-amber-100 overflow-hidden">
            <table className="w-full text-[11px]">
              <tbody>
                {Object.entries(updatedFields).map(([key, value], index) => (
                  <tr
                    key={key}
                    className={index % 2 === 0 ? 'bg-amber-50' : 'bg-white'}
                  >
                    <td className="px-2 py-1.5 font-medium text-amber-700 w-[140px]">
                      {formatEvalKey(key)}
                    </td>
                    <td className="px-2 py-1.5 text-amber-900">
                      {formatFieldValue(key, value)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Dialpad/BPO Call Details
// ============================================================================

const DialpadBPOCallDetails: React.FC<{
  input: Record<string, unknown>;
  output: Record<string, unknown>;
  startedAt?: string;
  matterId?: string;
  stepType: string;
}> = ({ input, output, startedAt, matterId, stepType }) => {
  const phoneNumber = (input.phone_number || output.phone_number || input.adjuster_phone) as string | undefined;
  const audioConversationId = output.audio_conversation_id as string | undefined;
  const timedOut = output.timed_out as boolean | undefined;
  const isBPO = stepType.toLowerCase().includes('bpo');

  // Extract evaluation from output (handles nested Result wrappers)
  const evaluation = React.useMemo(() => extractEvaluation(output), [output]);

  return (
    <div className="space-y-3">
      {/* Call Info */}
      <div className="space-y-1.5">
        <DetailRow label="Phone" value={formatPhoneNumber(phoneNumber)} />
        <DetailRow label="Called" value={startedAt ? formatDate(startedAt) : '—'} />
        <DetailRow
          label="Type"
          value={
            <span className={cn(
              'px-1.5 py-0.5 rounded text-[10px] font-medium',
              isBPO ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700',
            )}>
              {isBPO ? 'BPO Call' : 'RingCentral Call'}
            </span>
          }
        />
        {timedOut && (
          <DetailRow
            label="Status"
            value={<span className="text-amber-600 text-[11px]">Timed out waiting for evaluation</span>}
          />
        )}
        {audioConversationId && matterId && (
          <DetailRow
            label="Recording"
            value={
              <InspectArtifactButton
                artifactId={audioConversationId}
                artifactType={ArtifactType.AUDIO_CONVERSATION}
                matterId={matterId}
                compact
                icon={<Headphones className="h-3 w-3" />}
              />
            }
          />
        )}
      </div>

      {/* Evaluation */}
      <EvaluationTable evaluation={evaluation || {}} title="Evaluation Results" colorScheme="blue" />
    </div>
  );
};

// ============================================================================
// LLM Details (LLMBlock)
// ============================================================================

interface LLMConversationMessage {
  index: number;
  role: string;
  contentPreview: string;
  timestamp?: string;
  toolCallId?: string;
  toolCalls: Record<string, unknown>[];
}

interface HumanResponsePayload {
  approvalId?: string;
  action?: string;
  modifiedData?: Record<string, unknown>;
  userId?: string;
}

interface LLMToolExecution {
  iteration?: number;
  toolCallId?: string;
  toolName: string;
  toolType?: string;
  status: string;
  startedAt?: string;
  completedAt?: string;
  argsPreview?: string;
  resultPreview?: string;
  error?: string;
  humanResponse?: HumanResponsePayload;
}

const getApprovalIdFromExecution = (
  execution: LLMToolExecution | undefined,
): string | undefined => {
  if (!execution) return undefined;
  if (execution.humanResponse?.approvalId) {
    return execution.humanResponse.approvalId;
  }
  const parsedResult = parseJsonIfPossible(execution.resultPreview);
  if (!parsedResult || typeof parsedResult !== 'object' || Array.isArray(parsedResult)) {
    return undefined;
  }
  const approvalId = (parsedResult as Record<string, unknown>).approval_id;
  return typeof approvalId === 'string' ? approvalId : undefined;
};

const parseTimestampMs = (value?: string | null): number | null => {
  if (!value) return null;
  const ms = new Date(value).getTime();
  if (Number.isNaN(ms)) return null;
  return ms;
};

const sortPendingReviews = (
  reviews: CrashingOutPendingReview[],
): CrashingOutPendingReview[] => [...reviews].sort((a, b) => {
  const aMs = parseTimestampMs(a.created_at) ?? 0;
  const bMs = parseTimestampMs(b.created_at) ?? 0;
  return aMs - bMs;
});

const roleBadgeClass = (role: string) => {
  if (role === 'system') return 'bg-violet-100 text-violet-700';
  if (role === 'user') return 'bg-blue-100 text-blue-700';
  if (role === 'assistant' || role === 'ai') return 'bg-emerald-100 text-emerald-700';
  if (role === 'tool') return 'bg-amber-100 text-amber-700';
  return 'bg-zinc-100 text-zinc-700';
};

const roleBubbleClass = (role: string) => {
  if (role === 'system') return 'border-violet-200 bg-violet-50/60';
  if (role === 'user') return 'border-blue-200 bg-blue-50/70';
  if (role === 'assistant' || role === 'ai') return 'border-emerald-200 bg-emerald-50/70';
  if (role === 'tool') return 'border-amber-200 bg-amber-50/70';
  return 'border-zinc-200 bg-white';
};

const roleLabel = (role: string) => {
  if (role === 'ai') return 'assistant';
  return role;
};

const executionStatusClass = (status: string) => {
  if (status === 'completed') return 'bg-emerald-100 text-emerald-700';
  if (status === 'failed') return 'bg-red-100 text-red-700';
  if (status === 'requested') return 'bg-amber-100 text-amber-700';
  if (status === 'running') return 'bg-blue-100 text-blue-700';
  return 'bg-zinc-100 text-zinc-700';
};

const toolNameBadgeClass = (toolName: string) => {
  if (toolName === 'escalate_for_human_review') return 'bg-amber-100 text-amber-800 border-amber-200';
  if (toolName.startsWith('send_')) return 'bg-sky-100 text-sky-800 border-sky-200';
  if (toolName.startsWith('write_') || toolName.startsWith('update_')) return 'bg-violet-100 text-violet-800 border-violet-200';
  if (toolName.startsWith('get_') || toolName.startsWith('read_') || toolName.startsWith('list_')) {
    return 'bg-zinc-100 text-zinc-800 border-zinc-200';
  }
  return 'bg-indigo-100 text-indigo-800 border-indigo-200';
};

/** Extract reason / requested_action from the raw JSON args string of an escalate_for_human_review tool call. */
const extractEscalationFields = (
  rawArgs: string | undefined,
): { reason?: string; requestedAction?: string } => {
  if (!rawArgs) return {};
  try {
    const parsed = typeof rawArgs === 'string' ? JSON.parse(rawArgs) : rawArgs;
    return {
      reason: typeof parsed.reason === 'string' ? parsed.reason : undefined,
      requestedAction: typeof parsed.requested_action === 'string' ? parsed.requested_action : undefined,
    };
  } catch {
    return {};
  }
};

const ToolExecutionCard: React.FC<{
  rawToolName: string;
  execution?: LLMToolExecution;
  rawArgs?: string;
  rawResult?: string;
  matchedPendingReview?: CrashingOutPendingReview;
  reviewFormDataByApproval?: Record<string, Record<string, unknown>>;
  submittingReviewId?: string | null;
  onReviewFieldChange?: (
    approvalId: string,
    field: string,
    value: unknown,
  ) => void;
  onSubmitReview?: (approvalId: string) => Promise<void> | void;
}> = ({
  rawToolName,
  execution,
  rawArgs,
  rawResult,
  matchedPendingReview,
  reviewFormDataByApproval = {},
  submittingReviewId,
  onReviewFieldChange,
  onSubmitReview,
}) => {
  const toolLabel = getToolDisplayName(rawToolName);
  const [isExpanded, setIsExpanded] = React.useState(false);
  const hasDetails =
    hasMeaningfulPayload(rawArgs) ||
    hasMeaningfulPayload(rawResult) ||
    Boolean(execution?.humanResponse) ||
    Boolean(matchedPendingReview && rawToolName === 'escalate_for_human_review') ||
    Boolean(execution?.error);

  return (
    <div className="rounded-md border border-zinc-200 bg-white p-2.5">
      <button
        type="button"
        className={cn(
          'flex w-full items-center justify-between gap-2 text-left',
          hasDetails && 'cursor-pointer',
        )}
        onClick={() => {
          if (!hasDetails) return;
          setIsExpanded((current) => !current);
        }}
      >
        <div className="flex min-w-0 items-center gap-1.5">
          {hasDetails ? (
            isExpanded ? (
              <ChevronDown className="h-3.5 w-3.5 shrink-0 text-zinc-500" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 shrink-0 text-zinc-500" />
            )
          ) : (
            <div className="h-3.5 w-3.5 shrink-0" />
          )}
          <Wrench className="h-3 w-3 shrink-0" />
          <span
            className={cn(
              'inline-flex max-w-[200px] truncate items-center rounded-md border px-1.5 py-0.5 text-[10px] font-semibold',
              toolNameBadgeClass(rawToolName),
            )}
          >
            {toolLabel}
          </span>
          <span className="font-mono text-[10px] text-zinc-500 truncate">
            {rawToolName}
          </span>
        </div>
        <span
          className={cn(
            'shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium',
            executionStatusClass(execution?.status || 'requested'),
          )}
        >
          {execution?.status || 'requested'}
        </span>
      </button>

      {isExpanded ? (
        <>
          <div className="mt-1.5 space-y-1.5 border-t border-zinc-100 pt-1.5">
            {renderPayloadSection('Input', rawArgs, 'input')}
            {renderPayloadSection('Output', rawResult, 'output')}
          </div>

          {execution?.humanResponse && (
            <div className="mt-1.5 rounded-md border border-blue-200 bg-blue-50 p-2">
              <div className="text-[10px] font-medium text-blue-700 mb-1">
                Human Review Response
              </div>
              <div className="text-[10px] text-blue-900">
                Action: {execution.humanResponse.action || 'submitted'}
              </div>
              {execution.humanResponse.modifiedData &&
              Object.keys(execution.humanResponse.modifiedData).length > 0 ? (
                <pre className="mt-1 text-[10px] text-blue-900 whitespace-pre-wrap break-all font-mono max-h-48 overflow-y-auto overflow-x-hidden">
                  {JSON.stringify(execution.humanResponse.modifiedData, null, 2)}
                </pre>
              ) : null}
            </div>
          )}

          {rawToolName === 'escalate_for_human_review' &&
          matchedPendingReview &&
          !execution?.humanResponse ? (
            <div className="mt-2 border-t border-zinc-100 pt-2">
              <InlineHumanReviewCard
                review={matchedPendingReview}
                reviewFormDataByApproval={reviewFormDataByApproval}
                submittingReviewId={submittingReviewId}
                reason={extractEscalationFields(rawArgs).reason}
                requestedAction={extractEscalationFields(rawArgs).requestedAction}
                onReviewFieldChange={onReviewFieldChange}
                onSubmitReview={onSubmitReview}
              />
            </div>
          ) : null}

          {execution?.error ? (
            <div className="mt-1.5 text-[10px] text-red-700 bg-red-50 border border-red-100 rounded p-2">
              {execution.error}
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
};

const summarizeText = (text: string, maxChars = 180) => {
  if (text.length <= maxChars) return text;
  return `${text.slice(0, maxChars)}...`;
};

const hasMeaningfulPayload = (value?: string): boolean => {
  if (!value) return false;
  const trimmed = value.trim();
  if (!trimmed || trimmed === '{}' || trimmed === '[]') return false;
  return true;
};

const TOOL_DISPLAY_NAMES: Record<string, string> = {
  get_coverage_task_context: 'Get Coverage Task Context',
  refresh_coverage_task_context: 'Refresh Coverage Task Context',
  list_files: 'List Files',
  read_file: 'Read File',
  read_coverage: 'Read Coverage',
  get_lor_templates: 'Get LOR Templates',
  store_open_claim_context: 'Store Open Claim Context',
  write_coverage: 'Update Coverage',
  update_task: 'Update Task',
  send_voice_ai_call: 'Send Voice AI Call',
  send_bpo_call: 'Send BPO Call',
  write_lor: 'Write LOR',
  escalate_for_human_review: 'Escalate for Human Review',
};

const getToolDisplayName = (toolName: string): string => {
  if (TOOL_DISPLAY_NAMES[toolName]) {
    return TOOL_DISPLAY_NAMES[toolName];
  }
  return toolName
    .replace(/_/g, ' ')
    .replace(/^./, (char) => char.toUpperCase());
};

const parseJsonIfPossible = (value: unknown): unknown => {
  if (value === null || value === undefined) {
    return undefined;
  }
  if (typeof value !== 'string') {
    return value;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }
  try {
    return JSON.parse(trimmed);
  } catch {
    return undefined;
  }
};

const MAX_JSON_NODES_PER_LEVEL = 30;
const MAX_TEXT_PREVIEW_CHARS = 2000;

const formatPrimitiveJson = (value: unknown): string => {
  if (value === null) return 'null';
  if (typeof value === 'string') {
    if (value.length > MAX_TEXT_PREVIEW_CHARS) {
      return `"${value.slice(0, MAX_TEXT_PREVIEW_CHARS)}..."`;
    }
    return `"${value}"`;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return String(value);
};

const JsonTreeValue: React.FC<{
  value: unknown;
  depth?: number;
}> = ({ value, depth = 0 }) => {
  const isNested = depth > 0;

  if (value === null || typeof value !== 'object') {
    return (
      <span className="font-mono text-[10px] text-zinc-800 break-words break-all">
        {formatPrimitiveJson(value)}
      </span>
    );
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <span className="font-mono text-[10px] text-zinc-500">[]</span>;
    }
    const visibleItems = value.slice(0, MAX_JSON_NODES_PER_LEVEL);
    return (
      <div className={cn('space-y-1', isNested && 'ml-2 pl-2 border-l border-zinc-200')}>
        {visibleItems.map((item, index) => (
          <div key={`arr-${depth}-${index}`} className="grid grid-cols-[28px_1fr] gap-2 text-[10px]">
            <span className="font-mono text-zinc-500">[{index}]</span>
            <JsonTreeValue value={item} depth={depth + 1} />
          </div>
        ))}
        {value.length > visibleItems.length && (
          <div className="text-[10px] text-zinc-500">
            +{value.length - visibleItems.length} more items
          </div>
        )}
      </div>
    );
  }

  const entries = Object.entries(value as Record<string, unknown>);
  if (entries.length === 0) {
    return <span className="font-mono text-[10px] text-zinc-500">{'{}'}</span>;
  }
  const visibleEntries = entries.slice(0, MAX_JSON_NODES_PER_LEVEL);
  return (
    <div className={cn('space-y-1', isNested && 'ml-2 pl-2 border-l border-zinc-200')}>
      {visibleEntries.map(([key, nestedValue]) => (
        <div key={`${depth}-${key}`} className="grid grid-cols-[100px_1fr] sm:grid-cols-[140px_1fr] gap-2 text-[10px]">
          <span className="font-medium text-zinc-600 break-words">{formatFieldLabel(key)}</span>
          <JsonTreeValue value={nestedValue} depth={depth + 1} />
        </div>
      ))}
      {entries.length > visibleEntries.length && (
        <div className="text-[10px] text-zinc-500">
          +{entries.length - visibleEntries.length} more fields
        </div>
      )}
    </div>
  );
};

const formatFieldLabel = (key: string): string => key
  .replace(/_/g, ' ')
  .replace(/([a-z])([A-Z])/g, '$1 $2')
  .replace(/^./, (char) => char.toUpperCase());

const renderPayloadSection = (
  title: string,
  rawValue: unknown,
  tone: 'input' | 'output',
) => {
  const parsed = parseJsonIfPossible(rawValue);
  const toneClass = tone === 'input'
    ? 'border-blue-200 bg-blue-50/50'
    : 'border-emerald-200 bg-emerald-50/50';

  if (rawValue === null || rawValue === undefined || rawValue === '') {
    return (
      <div className={cn('rounded border p-2', toneClass)}>
        <div className="text-[10px] font-semibold uppercase tracking-wide text-zinc-600">{title}</div>
        <div className="mt-1 text-[10px] text-zinc-500">No data.</div>
      </div>
    );
  }

  if (parsed !== undefined) {
    return (
      <div className={cn('rounded border p-2 overflow-hidden', toneClass)}>
        <div className="text-[10px] font-semibold uppercase tracking-wide text-zinc-600">
          {title}
        </div>
        <div className="mt-1.5 overflow-x-auto overflow-y-hidden">
          <JsonTreeValue value={parsed} />
        </div>
      </div>
    );
  }

  return (
    <div className={cn('rounded border p-2 overflow-hidden', toneClass)}>
      <div className="text-[10px] font-semibold uppercase tracking-wide text-zinc-600">{title}</div>
      <pre className="mt-1 text-[10px] text-zinc-800 whitespace-pre-wrap break-all font-mono max-h-64 overflow-y-auto overflow-x-hidden">
        {safeStringify(rawValue)}
      </pre>
    </div>
  );
};

const InlineHumanReviewCard: React.FC<{
  review: CrashingOutPendingReview;
  reviewFormDataByApproval: Record<string, Record<string, unknown>>;
  submittingReviewId?: string | null;
  legacyTemporalStyle?: boolean;
  reason?: string | null;
  requestedAction?: string | null;
  onReviewFieldChange?: (
    approvalId: string,
    field: string,
    value: unknown,
  ) => void;
  onSubmitReview?: (approvalId: string) => Promise<void> | void;
}> = ({
  review,
  reviewFormDataByApproval,
  submittingReviewId,
  legacyTemporalStyle = false,
  reason,
  requestedAction,
  onReviewFieldChange,
  onSubmitReview,
}) => {
  const properties = (((review.response_schema || {}) as Record<string, unknown>)
    .properties || {}) as Record<string, SchemaProperty>;
  const requiredFields = new Set(
    ((((review.response_schema || {}) as Record<string, unknown>).required || []) as string[]),
  );
  const formValues = reviewFormDataByApproval[review.approval_id] || {};
  const isSubmitting = submittingReviewId === review.approval_id;
  const isEditable = Boolean(onReviewFieldChange) && Boolean(onSubmitReview);

  if (legacyTemporalStyle) {
    return (
      <div className="rounded-md border border-blue-200 bg-blue-50 p-2 sm:p-3 space-y-3">
        <div>
          <div className="text-xs font-semibold text-blue-800">
            {review.form_title || 'Human Review'}
          </div>
          {reason && (
            <div className="mt-1.5 rounded border border-blue-200 bg-white/60 p-2">
              <div className="text-[10px] font-semibold text-blue-700">Reason</div>
              <div className="mt-0.5 text-[11px] font-medium text-blue-900 break-words">{reason}</div>
            </div>
          )}
          {(requestedAction || review.form_description) && (
            <div className="mt-1.5 rounded border border-blue-200 bg-white/60 p-2">
              <div className="text-[10px] font-semibold text-blue-700">Requested Action</div>
              <div className="mt-0.5 text-[11px] font-medium text-blue-900 break-words">{requestedAction || review.form_description}</div>
            </div>
          )}
          <div className="text-[11px] text-blue-700 mt-1">
            Agent is waiting for a human response.
          </div>
        </div>

        <div className="space-y-2">
          {Object.entries(properties).map(([field, property]) => {
            const label = property.description || field.replaceAll('_', ' ');
            const value = formValues[field];
            const isRequired = requiredFields.has(field);
            const controlId = `${review.approval_id}-${field}`;

            if (property.enum && property.enum.length > 0) {
              return (
                <div key={field}>
                  <label htmlFor={controlId} className="block text-xs text-zinc-700 mb-1">
                    {label}
                    {isRequired ? ' *' : ''}
                  </label>
                  <select
                    id={controlId}
                    disabled={!isEditable}
                    value={(value as string) || ''}
                    onChange={(event) =>
                      onReviewFieldChange?.(review.approval_id, field, event.target.value)
                    }
                    className="w-full rounded border border-zinc-300 px-2 py-1.5 text-sm bg-white disabled:bg-zinc-100"
                  >
                    <option value="">Select</option>
                    {property.enum.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              );
            }

            if (property.type === 'boolean') {
              return (
                <div key={field} className="flex items-center gap-2 text-sm">
                  <input
                    id={controlId}
                    type="checkbox"
                    disabled={!isEditable}
                    checked={Boolean(value)}
                    onChange={(event) =>
                      onReviewFieldChange?.(review.approval_id, field, event.target.checked)
                    }
                  />
                  <label htmlFor={controlId}>{label}</label>
                </div>
              );
            }

            return (
              <div key={field}>
                <label htmlFor={controlId} className="block text-xs text-zinc-700 mb-1">
                  {label}
                  {isRequired ? ' *' : ''}
                </label>
                <input
                  id={controlId}
                  disabled={!isEditable}
                  type={
                    property.type === 'integer' || property.type === 'number'
                      ? 'number'
                      : 'text'
                  }
                  value={(value as string | number) || ''}
                  onChange={(event) =>
                    onReviewFieldChange?.(
                      review.approval_id,
                      field,
                      property.type === 'integer' || property.type === 'number'
                        ? Number(event.target.value)
                        : event.target.value,
                    )
                  }
                  className="w-full rounded border border-zinc-300 px-2 py-1.5 text-sm bg-white disabled:bg-zinc-100"
                />
              </div>
            );
          })}
        </div>

        <div className="flex justify-end">
          <Button
            size="sm"
            disabled={!isEditable || isSubmitting}
            onClick={() => {
              onSubmitReview?.(review.approval_id);
            }}
          >
            Submit Review
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-amber-50/60 p-3 sm:p-4 shadow-sm space-y-3">
      <div>
        <div className="flex items-center gap-2">
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-200 text-amber-800">
            <UserCheck className="h-3.5 w-3.5" />
          </div>
          <div className="text-xs font-semibold text-amber-900">
            {review.form_title || 'Human Review Required'}
          </div>
          {review.created_at && (
            <span className="ml-auto text-[10px] text-amber-700">
              {formatDate(review.created_at)}
            </span>
          )}
        </div>
        <div className="mt-1 text-[11px] text-amber-800">
          Agent is paused and waiting for a reviewer response.
        </div>
        {reason && (
          <div className="mt-1.5 rounded-md border border-amber-200 bg-white/80 p-2">
            <div className="text-[10px] font-semibold text-amber-700">Reason</div>
            <div className="mt-0.5 text-[11px] font-medium text-zinc-700 break-words">{reason}</div>
          </div>
        )}
        {(requestedAction || review.form_description) && (
          <div className="mt-1.5 rounded-md border border-amber-200 bg-white/80 p-2">
            <div className="text-[10px] font-semibold text-amber-700">Requested Action</div>
            <div className="mt-0.5 text-[11px] font-medium text-zinc-700 break-words">{requestedAction || review.form_description}</div>
          </div>
        )}
        {Object.keys(properties).length === 0 && (
          <div className="mt-1 text-[11px] text-amber-700">
            No structured fields provided. Submit to acknowledge and continue.
          </div>
        )}
      </div>

      <div className="space-y-2.5">
        {Object.entries(properties).map(([field, property]) => {
          const label = property.description || field.replaceAll('_', ' ');
          const value = formValues[field];
          const isRequired = requiredFields.has(field);
          const controlId = `${review.approval_id}-${field}`;

          if (property.enum && property.enum.length > 0) {
            return (
              <div key={field}>
                <label htmlFor={controlId} className="mb-1 flex items-center gap-1 text-[11px] font-medium text-zinc-700">
                  {label}
                  {isRequired ? <span className="text-red-600">*</span> : null}
                </label>
                <select
                  id={controlId}
                  disabled={!isEditable}
                  value={(value as string) || ''}
                  onChange={(event) =>
                    onReviewFieldChange?.(review.approval_id, field, event.target.value)
                  }
                  className="h-9 w-full rounded-md border border-amber-200 px-2.5 text-sm bg-white disabled:bg-zinc-100"
                >
                  <option value="">Select</option>
                  {property.enum.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            );
          }

          if (property.type === 'boolean') {
            return (
              <div key={field} className="flex items-center gap-2 rounded-md border border-amber-200 bg-white px-2.5 py-2 text-sm">
                <input
                  id={controlId}
                  type="checkbox"
                  disabled={!isEditable}
                  checked={Boolean(value)}
                  onChange={(event) =>
                    onReviewFieldChange?.(review.approval_id, field, event.target.checked)
                  }
                />
                <label htmlFor={controlId}>{label}</label>
              </div>
            );
          }

          return (
            <div key={field}>
              <label htmlFor={controlId} className="mb-1 flex items-center gap-1 text-[11px] font-medium text-zinc-700">
                {label}
                {isRequired ? <span className="text-red-600">*</span> : null}
              </label>
              <input
                id={controlId}
                disabled={!isEditable}
                type={
                  property.type === 'integer' || property.type === 'number'
                    ? 'number'
                    : 'text'
                }
                value={(value as string | number) || ''}
                onChange={(event) =>
                  onReviewFieldChange?.(
                    review.approval_id,
                    field,
                    property.type === 'integer' || property.type === 'number'
                      ? Number(event.target.value)
                      : event.target.value,
                  )
                }
                className="h-9 w-full rounded-md border border-amber-200 px-2.5 text-sm bg-white disabled:bg-zinc-100"
              />
            </div>
          );
        })}
      </div>

      <div className="flex justify-end">
        <Button
          size="sm"
          disabled={!isEditable || isSubmitting}
          onClick={() => {
            onSubmitReview?.(review.approval_id);
          }}
          className="bg-amber-600 hover:bg-amber-700 text-white"
        >
          {isSubmitting ? 'Submitting...' : 'Submit Review'}
        </Button>
      </div>
    </div>
  );
};

const LLMDetails: React.FC<{
  input: Record<string, unknown>;
  output: Record<string, unknown>;
  runStatus?: string;
  stepStatus?: StepTrace['status'];
  startedAt?: string;
  disableInternalScroll?: boolean;
  legacyTemporalStyle?: boolean;
  pendingReviews?: CrashingOutPendingReview[];
  reviewFormDataByApproval?: Record<string, Record<string, unknown>>;
  submittingReviewId?: string | null;
  onReviewFieldChange?: (
    approvalId: string,
    field: string,
    value: unknown,
  ) => void;
  onSubmitReview?: (approvalId: string) => Promise<void> | void;
}> = ({
  input,
  output,
  runStatus,
  stepStatus,
  startedAt,
  disableInternalScroll = false,
  legacyTemporalStyle = false,
  pendingReviews = [],
  reviewFormDataByApproval = {},
  submittingReviewId,
  onReviewFieldChange,
  onSubmitReview,
}) => {
  const model = (output.model_used || input.model) as string | undefined;
  const configuredTools = Array.isArray(input.tools)
    ? input.tools.filter((tool): tool is string => typeof tool === 'string')
    : [];
  const maxToolIterations =
    typeof input.max_tool_iterations === 'number' ? input.max_tool_iterations : undefined;
  const totalToolCalls =
    typeof output.total_tool_calls === 'number' ? output.total_tool_calls : undefined;

  const conversationMessages = React.useMemo<LLMConversationMessage[]>(() => {
    const traceMessages = toRecordArray(output.conversation_trace);
    if (traceMessages.length > 0) {
      return traceMessages.map((message, idx) => {
        const toolCalls = toRecordArray(message.tool_calls);
        return {
          index:
            typeof message.index === 'number'
              ? message.index
              : idx + 1,
          role:
            typeof message.role === 'string'
              ? message.role
              : 'unknown',
          contentPreview:
            (typeof message.content_preview === 'string' && message.content_preview) ||
            previewValue(message.content, 1200),
          timestamp:
            typeof message.timestamp === 'string'
              ? message.timestamp
              : undefined,
          toolCallId:
            typeof message.tool_call_id === 'string'
              ? message.tool_call_id
              : undefined,
          toolCalls,
        };
      });
    }

    const inputMessages = Array.isArray(input.messages) ? input.messages : [];
    return inputMessages.map((message, idx) => {
      const record = toRecord(message);
      return {
        index: idx + 1,
        role:
          (typeof record.role === 'string' && record.role) ||
          (typeof record.type === 'string' && record.type) ||
          'unknown',
        contentPreview: previewValue(record.content, 1200),
        timestamp:
          typeof record.timestamp === 'string'
            ? record.timestamp
            : undefined,
        toolCallId:
          typeof record.tool_call_id === 'string'
            ? record.tool_call_id
            : undefined,
        toolCalls: toRecordArray(record.tool_calls),
      };
    });
  }, [input.messages, output.conversation_trace]);

  const toolExecutions = React.useMemo<LLMToolExecution[]>(() => {
    const executions = toRecordArray(output.tool_executions);
    if (executions.length > 0) {
      return executions.map((execution) => ({
        iteration:
          typeof execution.iteration === 'number'
            ? execution.iteration
            : undefined,
        toolCallId:
          typeof execution.tool_call_id === 'string'
            ? execution.tool_call_id
            : undefined,
        toolName:
          typeof execution.tool_name === 'string'
            ? execution.tool_name
            : 'unknown_tool',
        toolType:
          typeof execution.tool_type === 'string'
            ? execution.tool_type
            : undefined,
        status:
          typeof execution.status === 'string'
            ? execution.status
            : 'completed',
        startedAt:
          typeof execution.started_at === 'string'
            ? execution.started_at
            : undefined,
        completedAt:
          typeof execution.completed_at === 'string'
            ? execution.completed_at
            : undefined,
        argsPreview:
          typeof execution.args_preview === 'string'
            ? execution.args_preview
            : previewValue(execution.args, 1000),
        resultPreview:
          typeof execution.result_preview === 'string'
            ? execution.result_preview
            : undefined,
        error:
          typeof execution.error === 'string'
            ? execution.error
            : undefined,
        humanResponse: (() => {
          const response = toRecord(execution.human_response);
          if (Object.keys(response).length === 0) {
            return undefined;
          }
          return {
            approvalId:
              typeof response.approval_id === 'string'
                ? response.approval_id
                : undefined,
            action:
              typeof response.action === 'string'
                ? response.action
                : undefined,
            modifiedData: toRecord(response.modified_data),
            userId:
              typeof response.user_id === 'string'
                ? response.user_id
                : undefined,
          };
        })(),
      }));
    }

    const toolCalls = toRecordArray(output.tool_calls);
    return toolCalls.map((toolCall) => ({
      toolCallId:
        typeof toolCall.id === 'string'
          ? toolCall.id
          : undefined,
      toolName:
        typeof toolCall.name === 'string'
          ? toolCall.name
          : 'unknown_tool',
      status: 'requested',
      argsPreview: previewValue(toolCall.args, 1000),
    }));
  }, [output.tool_calls, output.tool_executions]);

  const toolExecutionsByCallId = React.useMemo<Record<string, LLMToolExecution>>(() => {
    const map: Record<string, LLMToolExecution> = {};
    toolExecutions.forEach((execution) => {
      if (execution.toolCallId) {
        map[execution.toolCallId] = execution;
      }
    });
    return map;
  }, [toolExecutions]);

  const linkedToolCallIds = React.useMemo(() => {
    const ids = new Set<string>();
    conversationMessages.forEach((message) => {
      message.toolCalls.forEach((toolCall) => {
        const toolCallId = typeof toolCall.id === 'string' ? toolCall.id : null;
        if (toolCallId) {
          ids.add(toolCallId);
        }
      });
    });
    return ids;
  }, [conversationMessages]);

  const unlinkedExecutions = React.useMemo(
    () =>
      toolExecutions.filter(
        (execution) => !execution.toolCallId || !linkedToolCallIds.has(execution.toolCallId),
      ),
    [linkedToolCallIds, toolExecutions],
  );

  const showAllToolMessagesForRun = React.useMemo(() => {
    const normalizedRunStatus = (runStatus || '').toLowerCase();
    if (['failed', 'error', 'terminated', 'canceled', 'timed_out'].includes(normalizedRunStatus)) {
      return true;
    }
    return stepStatus === 'failed';
  }, [runStatus, stepStatus]);

  const visibleConversationMessages = React.useMemo(
    () =>
      conversationMessages.filter((message) => {
        if (message.role !== 'tool') {
          return true;
        }
        const text = message.contentPreview.trim();
        if (!message.toolCallId) {
          return true;
        }
        // Keep tool messages by default so intermediate tool updates are visible in
        // the chat stream. Only suppress empty stubs that add no information.
        const execution = toolExecutionsByCallId[message.toolCallId];
        if (!execution) {
          return true;
        }
        if (!text || text === '{}' || text === '[]') {
          return false;
        }
        if (showAllToolMessagesForRun) {
          return true;
        }
        // Keep failed tool messages visible so failed runs preserve full history.
        if (execution.status === 'failed') {
          return true;
        }
        // Keep in-progress tool messages visible in chat.
        if (execution.status === 'requested' || execution.status === 'running') {
          return true;
        }
        // For completed calls, hide only when structured output already has payload.
        // If structured output is empty, keep the chat message to avoid losing data.
        return !hasMeaningfulPayload(execution.resultPreview);
      }),
    [conversationMessages, showAllToolMessagesForRun, toolExecutionsByCallId],
  );

  const [expandedMessages, setExpandedMessages] = React.useState<Record<string, boolean>>({});
  const [expandedBubbleDetails, setExpandedBubbleDetails] = React.useState<Record<string, boolean>>({});
  const pendingReviewByApproval = React.useMemo(() => {
    const map: Record<string, CrashingOutPendingReview> = {};
    pendingReviews.forEach((review) => {
      map[review.approval_id] = review;
    });
    return map;
  }, [pendingReviews]);

  const escalationReviewByToolId = React.useMemo(() => {
    const assignments: Record<string, CrashingOutPendingReview> = {};
    const consumedApprovalIds = new Set<string>();
    const orderedPending = sortPendingReviews(pendingReviews);

    visibleConversationMessages.forEach((message) => {
      const messageId = `${message.index}-${message.role}-${message.toolCallId || 'na'}`;
      message.toolCalls.forEach((toolCall, idx) => {
        const rawToolName =
          typeof toolCall.name === 'string' ? toolCall.name : 'tool_call';
        if (rawToolName !== 'escalate_for_human_review') {
          return;
        }

        const toolCallId =
          typeof toolCall.id === 'string' ? toolCall.id : undefined;
        const toolId = `${messageId}-${toolCallId || idx}`;
        const execution = toolCallId ? toolExecutionsByCallId[toolCallId] : undefined;
        const explicitApprovalId = getApprovalIdFromExecution(execution);

        if (explicitApprovalId && pendingReviewByApproval[explicitApprovalId]) {
          assignments[toolId] = pendingReviewByApproval[explicitApprovalId];
          consumedApprovalIds.add(explicitApprovalId);
          return;
        }

        // Fallback only while still waiting on this escalation tool call:
        // bind the nearest unconsumed pending review created at/after the tool start.
        // Escalation tools stay "running" while waiting for a human review response.
        if (execution && execution.status !== 'requested' && execution.status !== 'running') {
          return;
        }

        const anchorMs =
          parseTimestampMs(execution?.startedAt)
          ?? parseTimestampMs(message.timestamp)
          ?? 0;
        const driftAllowanceMs = 2 * 60 * 1000;
        const fallbackReview = orderedPending.find((review) => {
          if (consumedApprovalIds.has(review.approval_id)) {
            return false;
          }
          const createdMs = parseTimestampMs(review.created_at);
          if (createdMs === null) {
            return orderedPending.length === 1;
          }
          return createdMs >= (anchorMs - driftAllowanceMs);
        });
        if (fallbackReview) {
          assignments[toolId] = fallbackReview;
          consumedApprovalIds.add(fallbackReview.approval_id);
        }
      });
    });

    return assignments;
  }, [
    pendingReviewByApproval,
    pendingReviews,
    toolExecutionsByCallId,
    visibleConversationMessages,
  ]);

  const chatScrollRef = React.useRef<HTMLDivElement | null>(null);
  const shouldStickToBottomRef = React.useRef(true);

  React.useEffect(() => {
    if (disableInternalScroll) {
      shouldStickToBottomRef.current = false;
      return undefined;
    }

    const node = chatScrollRef.current;
    if (!node) {
      return undefined;
    }

    const updateStickiness = () => {
      const distanceToBottom =
        node.scrollHeight - node.scrollTop - node.clientHeight;
      shouldStickToBottomRef.current = distanceToBottom <= 48;
    };

    updateStickiness();
    node.addEventListener('scroll', updateStickiness, { passive: true });
    return () => node.removeEventListener('scroll', updateStickiness);
  }, [disableInternalScroll]);

  React.useEffect(() => {
    if (disableInternalScroll) {
      return undefined;
    }

    const node = chatScrollRef.current;
    if (!node || !shouldStickToBottomRef.current) {
      return undefined;
    }

    const frameId = window.requestAnimationFrame(() => {
      node.scrollTop = node.scrollHeight;
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [
    disableInternalScroll,
    visibleConversationMessages.length,
    pendingReviews.length,
    toolExecutions.length,
  ]);

  if (legacyTemporalStyle) {
    return (
      <div className="space-y-3.5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1 rounded bg-zinc-100 px-2 py-0.5 text-[10px] font-medium text-zinc-700">
            Model: {model || '—'}
          </span>
          <span className="inline-flex items-center gap-1 rounded bg-zinc-100 px-2 py-0.5 text-[10px] font-medium text-zinc-700">
            Started: {startedAt ? formatDate(startedAt) : '—'}
          </span>
          <span className="inline-flex items-center gap-1 rounded bg-zinc-100 px-2 py-0.5 text-[10px] font-medium text-zinc-700">
            Tool Calls: {totalToolCalls ?? toolExecutions.length}
          </span>
          {maxToolIterations !== undefined && (
            <span className="inline-flex items-center gap-1 rounded bg-zinc-100 px-2 py-0.5 text-[10px] font-medium text-zinc-700">
              Max Iterations: {maxToolIterations}
            </span>
          )}
        </div>

        {configuredTools.length > 0 && (
          <div className="border-t border-zinc-100 pt-2">
            <div className="mb-1.5 text-[10px] font-medium text-zinc-500">
              Available Tools
            </div>
            <div className="flex flex-wrap gap-1">
              {configuredTools.map((tool) => (
                <span
                  key={tool}
                  className="inline-flex items-center rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] text-zinc-700"
                >
                  {getToolDisplayName(tool)}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="border-t border-zinc-100 pt-2">
          <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-medium text-zinc-500">
            <Bot className="h-3 w-3" />
            Agent Chat
          </div>
          <div
            ref={chatScrollRef}
            className={cn(
              'space-y-2.5 rounded border border-zinc-200 bg-zinc-50 p-1.5 sm:p-2.5',
              disableInternalScroll
                ? 'overflow-visible'
                : 'max-h-[50vh] overflow-y-auto sm:max-h-[620px]',
            )}
          >
            {visibleConversationMessages.length === 0 && (
              <div className="text-[11px] text-zinc-500">
                No conversation messages recorded yet.
              </div>
            )}

            <AnimatePresence initial={false}>
              {visibleConversationMessages.map((message, messageIdx) => {
                const isUserLike = message.role === 'user';
                const messageId = `${message.index}-${message.role}-${message.toolCallId || 'na'}`;
                const isExpanded = Boolean(expandedMessages[messageId]);
                const fullText = message.contentPreview || '—';
                const trimmedText = fullText.trim();
                const shouldHideTextForToolStub = (
                  (message.role === 'assistant' || message.role === 'ai')
                  && message.toolCalls.length > 0
                  && (trimmedText === '' || trimmedText === '[]' || trimmedText === '{}')
                );
                const shouldTruncate = fullText.length > 700;
                const displayText =
                  shouldTruncate && !isExpanded ? summarizeText(fullText, 700) : fullText;

                return (
                  <motion.div
                    key={messageId}
                    className={cn('flex', isUserLike ? 'justify-end' : 'justify-start')}
                    layout
                    initial={{ opacity: 0, y: 12, scale: 0.99 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{
                      duration: 0.24,
                      delay: Math.min(messageIdx * 0.025, 0.14),
                      ease: [0.2, 0.8, 0.2, 1],
                    }}
                  >
                    <div
                      className={cn(
                        'w-full max-w-[96%] rounded-lg border p-2.5 shadow-[0_1px_0_rgba(0,0,0,0.04)]',
                        roleBubbleClass(message.role),
                      )}
                    >
                      <div className="mb-1 flex items-center justify-between gap-2">
                        <span
                          className={cn(
                            'rounded px-1.5 py-0.5 text-[10px] font-medium uppercase',
                            roleBadgeClass(message.role),
                          )}
                        >
                          {roleLabel(message.role)}
                        </span>
                        <div className="text-[10px] text-zinc-500">
                          {message.timestamp ? formatDate(message.timestamp) : ''}
                        </div>
                      </div>

                      {!shouldHideTextForToolStub && (
                        <div className="whitespace-pre-wrap break-words text-[11px] leading-relaxed text-zinc-800">
                          {displayText}
                        </div>
                      )}

                      {shouldTruncate && !shouldHideTextForToolStub && (
                        <button
                          type="button"
                          className="mt-1 text-[10px] font-medium text-zinc-600 hover:text-zinc-900"
                          onClick={() =>
                            setExpandedMessages((prev) => ({
                              ...prev,
                              [messageId]: !prev[messageId],
                            }))
                          }
                        >
                          {isExpanded ? 'Show less' : 'Show more'}
                        </button>
                      )}

                      {message.toolCalls.length > 0 && (
                        <div className="mt-2 space-y-2">
                          {message.toolCalls.map((toolCall, idx) => {
                            const toolCallId =
                              typeof toolCall.id === 'string' ? toolCall.id : undefined;
                            const execution =
                              toolCallId ? toolExecutionsByCallId[toolCallId] : undefined;
                            const toolId = `${messageId}-${toolCallId || idx}`;
                            const rawToolName =
                              typeof toolCall.name === 'string' ? toolCall.name : 'tool_call';
                            const rawArgs =
                              execution?.argsPreview || previewValue(toolCall.args, 2000);
                            const rawResult = execution?.resultPreview;
                            const matchedPendingReview = escalationReviewByToolId[toolId];

                            return (
                              <div
                                key={toolId}
                                className="overflow-hidden rounded border border-zinc-200 bg-white p-2"
                              >
                                <div className="flex items-center justify-between gap-2 pb-1">
                                  <div className="flex items-center gap-1 text-[10px] font-semibold text-zinc-700">
                                    <Wrench className="h-3 w-3" />
                                    {getToolDisplayName(rawToolName)}
                                    <span className="font-normal text-zinc-400">
                                      ({rawToolName})
                                    </span>
                                  </div>
                                  <span
                                    className={cn(
                                      'rounded px-1.5 py-0.5 text-[10px] font-medium',
                                      executionStatusClass(execution?.status || 'requested'),
                                    )}
                                  >
                                    {execution?.status || 'requested'}
                                  </span>
                                </div>

                                <div className="mt-1.5 space-y-1.5 border-t border-zinc-100 pt-1.5">
                                  {renderPayloadSection('Input', rawArgs, 'input')}
                                  {renderPayloadSection('Output', rawResult, 'output')}
                                </div>

                                {execution?.humanResponse && (
                                  <div className="mt-1.5 rounded border border-blue-100 bg-blue-50 p-2">
                                    <div className="mb-1 text-[10px] font-medium text-blue-700">
                                      Human Review Response
                                    </div>
                                    <div className="text-[10px] text-blue-900">
                                      Action: {execution.humanResponse.action || 'submitted'}
                                    </div>
                                    {execution.humanResponse.modifiedData
                                      && Object.keys(execution.humanResponse.modifiedData).length > 0 && (
                                      <pre className="mt-1 max-h-24 overflow-y-auto whitespace-pre-wrap break-words font-mono text-[10px] text-blue-900">
                                        {JSON.stringify(
                                          execution.humanResponse.modifiedData,
                                          null,
                                          2,
                                        )}
                                      </pre>
                                    )}
                                  </div>
                                )}

                                {rawToolName === 'escalate_for_human_review'
                                  && matchedPendingReview
                                  && !execution?.humanResponse && (
                                  <div className="mt-2 border-t border-zinc-100 pt-2">
                                    <InlineHumanReviewCard
                                      review={matchedPendingReview}
                                      reviewFormDataByApproval={reviewFormDataByApproval}
                                      submittingReviewId={submittingReviewId}
                                      legacyTemporalStyle
                                      reason={extractEscalationFields(rawArgs).reason}
                                      requestedAction={extractEscalationFields(rawArgs).requestedAction}
                                      onReviewFieldChange={onReviewFieldChange}
                                      onSubmitReview={onSubmitReview}
                                    />
                                  </div>
                                )}

                                {execution?.error && (
                                  <div className="mt-1.5 rounded border border-red-100 bg-red-50 p-2 text-[10px] text-red-700">
                                    {execution.error}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>

            {unlinkedExecutions.length > 0 && (
              <div className="space-y-1.5 pt-1">
                {unlinkedExecutions.map((execution, idx) => (
                  <div
                    key={`${execution.toolName}-${execution.toolCallId || idx}-unlinked`}
                    className="rounded border border-zinc-200 bg-white p-2.5"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-[11px] font-medium text-zinc-900">
                        <span className="inline-flex items-center gap-1">
                          <Wrench className="h-3 w-3 text-zinc-500" />
                          {getToolDisplayName(execution.toolName)}
                          <span className="font-normal text-zinc-400">
                            ({execution.toolName})
                          </span>
                        </span>
                      </div>
                      <span
                        className={cn(
                          'rounded px-1.5 py-0.5 text-[10px] font-medium',
                          executionStatusClass(execution.status),
                        )}
                      >
                        {execution.status}
                      </span>
                    </div>
                    <div className="mt-1.5 space-y-1.5">
                      {renderPayloadSection('Input', execution.argsPreview, 'input')}
                      {renderPayloadSection('Output', execution.resultPreview, 'output')}
                    </div>
                    {execution.error && (
                      <div className="mt-1.5 rounded border border-red-100 bg-red-50 p-2 text-[10px] text-red-700">
                        {execution.error}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3.5">
      <div>
        <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-medium text-zinc-500">
          <Bot className="h-3 w-3" />
          {model ? `Agent Chat · ${model}` : 'Agent Chat'}
        </div>
        <div
          ref={chatScrollRef}
          className={cn(
            'space-y-2.5 rounded-lg border border-zinc-200 bg-zinc-50 p-2 sm:p-3',
            disableInternalScroll
              ? 'overflow-visible'
              : 'max-h-[58vh] overflow-y-auto overflow-x-hidden sm:max-h-[700px]',
          )}
        >
          {visibleConversationMessages.length === 0 && (
            <div className="text-[11px] text-zinc-500">
              No conversation messages recorded yet.
            </div>
          )}

          <AnimatePresence initial={false}>
            {visibleConversationMessages.map((message, messageIdx) => {
            const isUserLike = message.role === 'user';
            const messageId = `${message.index}-${message.role}-${message.toolCallId || 'na'}`;
            const isExpanded = Boolean(expandedMessages[messageId]);
            const fullText = message.contentPreview || '—';
            const trimmedText = fullText.trim();
            const shouldHideTextForToolStub = (
              (message.role === 'assistant' || message.role === 'ai')
              && message.toolCalls.length > 0
              && (trimmedText === '' || trimmedText === '[]' || trimmedText === '{}')
            );
            const linkedExecution =
              message.toolCallId ? toolExecutionsByCallId[message.toolCallId] : undefined;
            const hasExpandableDetails =
              message.toolCalls.length > 0 || Boolean(linkedExecution);
            const isBubbleDetailsExpanded = Boolean(expandedBubbleDetails[messageId]);
            const shouldTruncate = fullText.length > 700;
            const displayText =
              shouldTruncate && !isExpanded ? summarizeText(fullText, 700) : fullText;

              return (
              <motion.div
                key={messageId}
                className={cn('flex', isUserLike ? 'justify-end' : 'justify-start')}
                layout
                initial={{ opacity: 0, y: 12, scale: 0.99 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{
                  duration: 0.24,
                  delay: Math.min(messageIdx * 0.025, 0.14),
                  ease: [0.2, 0.8, 0.2, 1],
                }}
              >
                <div
                  className={cn(
                    'w-full max-w-[96%] rounded-lg border p-2.5 shadow-[0_1px_0_rgba(0,0,0,0.04)]',
                    roleBubbleClass(message.role),
                  )}
                >
                  <div className="mb-1 flex items-center justify-between gap-2">
                    <span
                      className={cn(
                        'px-1.5 py-0.5 rounded text-[10px] font-medium uppercase',
                        roleBadgeClass(message.role),
                      )}
                    >
                      {roleLabel(message.role)}
                    </span>
                    <div className="text-[10px] text-zinc-500">
                      {message.timestamp ? formatDate(message.timestamp) : ''}
                    </div>
                  </div>

                  {!shouldHideTextForToolStub && (
                    <div className="text-[12px] leading-relaxed text-zinc-800 whitespace-pre-wrap break-words">
                      {displayText}
                    </div>
                  )}

                  {shouldTruncate && !shouldHideTextForToolStub && (
                    <button
                      type="button"
                      className="mt-1 text-[10px] font-medium text-zinc-600 hover:text-zinc-900"
                      onClick={() =>
                        setExpandedMessages((prev) => ({
                          ...prev,
                          [messageId]: !prev[messageId],
                        }))
                      }
                    >
                      {isExpanded ? 'Show less' : 'Show more'}
                    </button>
                  )}

                  {hasExpandableDetails && (
                    <button
                      type="button"
                      className="mt-2 inline-flex items-center gap-1 text-[10px] font-medium text-zinc-600 hover:text-zinc-900"
                      onClick={() =>
                        setExpandedBubbleDetails((prev) => ({
                          ...prev,
                          [messageId]: !prev[messageId],
                        }))
                      }
                    >
                      {isBubbleDetailsExpanded ? (
                        <ChevronDown className="h-3 w-3" />
                      ) : (
                        <ChevronRight className="h-3 w-3" />
                      )}
                      {isBubbleDetailsExpanded ? 'Hide input/output' : 'Show input/output'}
                    </button>
                  )}

                  {isBubbleDetailsExpanded && message.toolCalls.length > 0 && (
                    <div className="mt-2 space-y-2">
                      {message.toolCalls.map((toolCall, idx) => {
                        const toolCallId =
                          typeof toolCall.id === 'string' ? toolCall.id : undefined;
                        const execution =
                          toolCallId ? toolExecutionsByCallId[toolCallId] : undefined;
                        const toolId = `${messageId}-${toolCallId || idx}`;
                        const rawToolName =
                          typeof toolCall.name === 'string' ? toolCall.name : 'tool_call';
                        const rawArgs = execution?.argsPreview || previewValue(toolCall.args, 2000);
                        const rawResult = execution?.resultPreview;
                        const matchedPendingReview = escalationReviewByToolId[toolId];

                        return (
                          <ToolExecutionCard
                            key={toolId}
                            rawToolName={rawToolName}
                            execution={execution}
                            rawArgs={rawArgs}
                            rawResult={rawResult}
                            matchedPendingReview={matchedPendingReview}
                            reviewFormDataByApproval={reviewFormDataByApproval}
                            submittingReviewId={submittingReviewId}
                            onReviewFieldChange={onReviewFieldChange}
                            onSubmitReview={onSubmitReview}
                          />
                        );
                      })}
                    </div>
                  )}

                  {isBubbleDetailsExpanded &&
                  message.toolCalls.length === 0 &&
                  linkedExecution ? (
                    <div className="mt-2 space-y-2">
                      <ToolExecutionCard
                        rawToolName={linkedExecution.toolName}
                        execution={linkedExecution}
                        rawArgs={linkedExecution.argsPreview}
                        rawResult={linkedExecution.resultPreview}
                        matchedPendingReview={
                          linkedExecution.toolName === 'escalate_for_human_review'
                            ? getApprovalIdFromExecution(linkedExecution)
                              ? pendingReviewByApproval[getApprovalIdFromExecution(linkedExecution) as string]
                              : undefined
                            : undefined
                        }
                        reviewFormDataByApproval={reviewFormDataByApproval}
                        submittingReviewId={submittingReviewId}
                        onReviewFieldChange={onReviewFieldChange}
                        onSubmitReview={onSubmitReview}
                      />
                    </div>
                  ) : null}
                </div>
              </motion.div>
              );
            })}
          </AnimatePresence>

          {unlinkedExecutions.length > 0 && (
            <div className="space-y-1.5 pt-1">
              {unlinkedExecutions.map((execution, idx) => {
                return (
                <div
                  key={`${execution.toolName}-${execution.toolCallId || idx}-unlinked`}
                  className="rounded border border-zinc-200 bg-white p-2.5"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-[11px] font-medium text-zinc-900">
                      <span className="inline-flex items-center gap-1">
                        <Wrench className="h-3 w-3 text-zinc-500" />
                        {getToolDisplayName(execution.toolName)}
                        <span className="text-zinc-400 font-normal">({execution.toolName})</span>
                      </span>
                    </div>
                    <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-medium', executionStatusClass(execution.status))}>
                      {execution.status}
                    </span>
                  </div>
                  <div className="mt-1.5 space-y-1.5">
                    {renderPayloadSection('Input', execution.argsPreview, 'input')}
                    {renderPayloadSection('Output', execution.resultPreview, 'output')}
                  </div>
                  {execution.error && (
                    <div className="mt-1.5 text-[10px] text-red-700 bg-red-50 border border-red-100 rounded p-2">
                      {execution.error}
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Step Details View - Routes to the appropriate custom renderer
// ============================================================================

const StepDetailsView: React.FC<{
  trace: StepTrace;
  runStatus?: string;
  matterId?: string;
  disableNestedScroll?: boolean;
  legacyTemporalStyle?: boolean;
  pendingReviews?: CrashingOutPendingReview[];
  reviewFormDataByApproval?: Record<string, Record<string, unknown>>;
  submittingReviewId?: string | null;
  onReviewFieldChange?: (
    approvalId: string,
    field: string,
    value: unknown,
  ) => void;
  onSubmitReview?: (approvalId: string) => Promise<void> | void;
}> = ({
  trace,
  runStatus,
  matterId,
  disableNestedScroll = false,
  legacyTemporalStyle = false,
  pendingReviews,
  reviewFormDataByApproval,
  submittingReviewId,
  onReviewFieldChange,
  onSubmitReview,
}) => {
  const [showRawJson, setShowRawJson] = React.useState(false);
  const stepType = trace.step_type.toLowerCase();
  const hasStructuredInput = Boolean(trace.input && Object.keys(trace.input).length > 0);
  const hasStructuredOutput = Boolean(trace.output && Object.keys(trace.output).length > 0);

  // Merge input and output for the raw JSON view
  const rawData = {
    ...(trace.input && { input: trace.input }),
    ...(trace.output && { output: trace.output }),
  };

  const renderCustomView = () => {
    // Voice AI Call (BlandBlock)
    if (stepType.includes('bland')) {
      return (
        <VoiceAICallDetails
          input={trace.input || {}}
          output={trace.output || {}}
          startedAt={trace.started_at}
          matterId={matterId}
        />
      );
    }

    // Email (SendEmailBlock)
    if (stepType.includes('email')) {
      return (
        <EmailDetails
          input={trace.input || {}}
          output={trace.output || {}}
          startedAt={trace.started_at}
        />
      );
    }

    // Dialpad or BPO Call
    if (stepType.includes('dialpad') || stepType.includes('bpo')) {
      return (
        <DialpadBPOCallDetails
          input={trace.input || {}}
          output={trace.output || {}}
          startedAt={trace.started_at}
          matterId={matterId}
          stepType={trace.step_type}
        />
      );
    }

    // Notification (SendTaskNotificationBlock)
    if (stepType.includes('notification')) {
      return (
        <NotificationDetails
          input={trace.input || {}}
          startedAt={trace.started_at}
        />
      );
    }

    // LLM driver block
    if (stepType.includes('llm')) {
      return (
        <LLMDetails
          input={trace.input || {}}
          output={trace.output || {}}
          runStatus={runStatus}
          stepStatus={trace.status}
          startedAt={trace.started_at}
          disableInternalScroll={disableNestedScroll}
          legacyTemporalStyle={legacyTemporalStyle}
          pendingReviews={pendingReviews}
          reviewFormDataByApproval={reviewFormDataByApproval}
          submittingReviewId={submittingReviewId}
          onReviewFieldChange={onReviewFieldChange}
          onSubmitReview={onSubmitReview}
        />
      );
    }

    // Task Field Update (UpdateTaskBlock) - updates due_date, name, etc.
    if (stepType === 'updatetaskblock' || stepType.includes('updatetask') || stepType.includes('taskduedate')) {
      return (
        <TaskFieldUpdateDetails
          input={trace.input || {}}
          startedAt={trace.started_at}
        />
      );
    }

    // Task Comment/Activity (SendTaskUpdateBlock) - adds comment to task
    if (stepType.includes('sendtaskupdate')) {
      return (
        <TaskCommentDetails
          input={trace.input || {}}
          startedAt={trace.started_at}
        />
      );
    }

    // Default: show a simple summary with basic info
    if (legacyTemporalStyle) {
      return (
        <div className="space-y-1.5">
          <DetailRow label="Type" value={getStepTypeLabel(trace.step_type)} />
          <DetailRow
            label="Time"
            value={trace.started_at ? formatDate(trace.started_at) : '—'}
          />
          {trace.duration_ms ? (
            <DetailRow label="Duration" value={formatDuration(trace.duration_ms)} />
          ) : null}
        </div>
      );
    }

    return (
      <div className="space-y-3">
        <div className="space-y-1.5">
          <DetailRow label="Type" value={getStepTypeLabel(trace.step_type)} />
          <DetailRow label="Time" value={trace.started_at ? formatDate(trace.started_at) : '—'} />
          <DetailRow label="Status" value={trace.status} />
          {trace.duration_ms ? (
            <DetailRow label="Duration" value={formatDuration(trace.duration_ms)} />
          ) : null}
        </div>

        {(hasStructuredInput || hasStructuredOutput) ? (
          <div className="space-y-1.5 border-t border-zinc-100 pt-2">
            {hasStructuredInput ? renderPayloadSection('Input', trace.input, 'input') : null}
            {hasStructuredOutput ? renderPayloadSection('Output', trace.output, 'output') : null}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className="space-y-3">
      {/* Error display */}
      {trace.error_message && (
        <div className="rounded border border-red-200 bg-red-50 p-2">
          <div className="text-[10px] font-medium text-red-800 mb-1">Error:</div>
          <div className="text-[10px] text-red-700 font-mono">{trace.error_message}</div>
        </div>
      )}

      {/* Custom view or raw JSON */}
      {showRawJson ? (
        <div className="space-y-2">
          <pre className="text-[10px] bg-zinc-900 text-zinc-100 rounded p-3 overflow-x-auto font-mono whitespace-pre-wrap max-h-[300px] overflow-y-auto">
            {legacyTemporalStyle
              ? JSON.stringify(rawData, null, 2)
              : safeStringify(rawData)}
          </pre>
        </div>
      ) : (
        renderCustomView()
      )}

      {/* Toggle button */}
      <div className="flex justify-end pt-1 border-t border-zinc-100">
        <button
          type="button"
          onClick={() => setShowRawJson(!showRawJson)}
          className="text-[10px] text-zinc-500 hover:text-zinc-700 flex items-center gap-1"
        >
          <Code className="h-3 w-3" />
          {showRawJson ? 'Show Formatted' : 'View Raw JSON'}
        </button>
      </div>
    </div>
  );
};

/** Extract summary info from step input/output for display */
const getStepSummary = (
  stepType: string,
  input?: Record<string, unknown>,
  output?: Record<string, unknown>
): { inputSummary?: string; outputSummary?: string } => {
  const result: { inputSummary?: string; outputSummary?: string } = {};

  // Extract input summary based on step type
  if (input) {
    const lowerType = stepType.toLowerCase();
    if (lowerType.includes('bland') || lowerType.includes('voice')) {
      const phone = input.phone_number || input.to_phone;
      if (phone) result.inputSummary = `Called ${phone}`;
    } else if (lowerType.includes('dialpad')) {
      const phone = input.adjuster_phone || input.phone_number;
      if (phone) result.inputSummary = `RingCentral to ${phone}`;
    } else if (lowerType.includes('bpo')) {
      const phone = input.phone_number;
      if (phone) result.inputSummary = `BPO call to ${phone}`;
    } else if (lowerType.includes('email')) {
      const recipient = input.recipient_email || input.to_email;
      if (recipient) result.inputSummary = `Email to ${recipient}`;
    } else if (lowerType.includes('text') || lowerType.includes('sms')) {
      const phone = input.phone_number;
      if (phone) result.inputSummary = `Text to ${phone}`;
    } else if (lowerType.includes('llm')) {
      const { model } = input;
      if (typeof model === 'string' && model.length > 0) {
        result.inputSummary = `Model: ${model}`;
      }
    }
  }

  // Extract output summary based on step type
  if (output) {
    const lowerType = stepType.toLowerCase();
    if (lowerType.includes('bland') || lowerType.includes('voice')) {
      // Voice AI call output
      const status = output.call_status || output.status;
      const summary = output.summary || output.call_summary;
      if (output.outcome) {
        result.outputSummary = String(output.outcome);
      } else if (summary) {
        // Truncate long summaries
        const summaryStr = String(summary);
        result.outputSummary = summaryStr.length > 80
          ? `${summaryStr.slice(0, 80)}...`
          : summaryStr;
      } else if (status) {
        result.outputSummary = String(status);
      }
    } else if (lowerType.includes('dialpad')) {
      const outcome = output.outcome || output.call_outcome;
      const notes = output.notes || output.call_notes;
      if (outcome) {
        result.outputSummary = String(outcome);
      } else if (notes) {
        const notesStr = String(notes);
        result.outputSummary = notesStr.length > 80
          ? `${notesStr.slice(0, 80)}...`
          : notesStr;
      }
    } else if (lowerType.includes('bpo')) {
      // BPO evaluation output
      const evaluation = output.evaluation as Record<string, unknown> | undefined;
      const outcome = evaluation?.outcome || output.outcome;
      const acknowledged = evaluation?.acknowledged || output.acknowledged;
      if (outcome) {
        result.outputSummary = String(outcome);
      } else if (acknowledged !== undefined) {
        result.outputSummary = acknowledged ? 'Acknowledged' : 'Not acknowledged';
      }
    } else if (lowerType.includes('email')) {
      const sent = output.sent || output.success;
      const messageId = output.message_id;
      if (sent) {
        result.outputSummary = 'Sent';
      } else if (messageId) {
        result.outputSummary = 'Sent';
      }
    } else if (lowerType.includes('text') || lowerType.includes('sms')) {
      const sent = output.sent || output.success;
      if (sent) {
        result.outputSummary = 'Sent';
      }
    } else if (lowerType.includes('llm')) {
      const { total_tool_calls: totalToolCalls, content } = output;
      if (typeof totalToolCalls === 'number') {
        result.outputSummary = `${totalToolCalls} tool call${totalToolCalls === 1 ? '' : 's'}`;
      } else if (typeof content === 'string' && content.length > 0) {
        result.outputSummary = content.length > 80
          ? `${content.slice(0, 80)}...`
          : content;
      }
    }
  }

  return result;
};

export const CrashingOutExecutionHistory: React.FC<CrashingOutExecutionHistoryProps> = ({
  traces,
  runStatus,
  matterId,
  maxItems = 10,
  onMakeCall,
  onEnterResults,
  pendingReviews = [],
  reviewFormDataByApproval = {},
  submittingReviewId,
  onReviewFieldChange,
  onSubmitReview,
  defaultExpandLast = false,
  autoScrollToLatest = true,
  focusPendingReview = false,
  legacyTemporalStyle = false,
}) => {
  const [showAll, setShowAll] = React.useState(defaultExpandLast);
  const [showReviewHistory, setShowReviewHistory] = React.useState(false);
  const [expandedStepId, setExpandedStepId] = React.useState<
    string | number | null
  >(null);

  // Auto-expand the last LLM block when defaultExpandLast is set
  const hasAutoExpanded = React.useRef(false);
  React.useEffect(() => {
    if (defaultExpandLast && traces.length > 0 && !hasAutoExpanded.current) {
      hasAutoExpanded.current = true;
      const lastLLM = [...traces].reverse().find((t) => t.step_type === 'LLMBlock');
      if (lastLLM) {
        setExpandedStepId(lastLLM.step_id);
      }
    }
  }, [defaultExpandLast, traces]);

  // Filter to show only relevant steps (not Wait steps unless they're the only ones)
  const relevantTraces = traces.filter(
    (t) => t.step_type !== 'Wait' || t.status === 'waiting',
  );

  const displayTraces = showAll
    ? relevantTraces
    : relevantTraces.slice(legacyTemporalStyle ? 0 : -maxItems);
  const hasMore = relevantTraces.length > maxItems;
  const latestStepRef = React.useRef<HTMLDivElement | null>(null);
  const showReviewFirst = !legacyTemporalStyle && focusPendingReview && pendingReviews.length > 0;
  const shouldAutoScrollHistory =
    !legacyTemporalStyle
    && autoScrollToLatest
    && !(showReviewFirst && showReviewHistory);
  const useUnifiedScroll = !legacyTemporalStyle && showReviewFirst && showReviewHistory;

  // Build a lookup from approval_id → { reason, requestedAction } by scanning traces.
  // Handles both temporal-style (nested tool_executions) and postgres-style (flat step traces).
  const escalationFieldsByApprovalId = React.useMemo<
    Record<string, { reason?: string; requestedAction?: string }>
  >(() => {
    const map: Record<string, { reason?: string; requestedAction?: string }> = {};
    const unmatchedFields: { reason?: string; requestedAction?: string }[] = [];
    traces.forEach((trace) => {
      // Postgres-style: flat step trace with step_type and review_approval_id at the top level
      if (trace.step_type === 'escalate_for_human_review') {
        const traceInput = toRecord(trace.input);
        const fields: { reason?: string; requestedAction?: string } = {
          reason: typeof traceInput.reason === 'string' ? traceInput.reason : undefined,
          requestedAction: typeof traceInput.requested_action === 'string' ? traceInput.requested_action : undefined,
        };
        if (fields.reason || fields.requestedAction) {
          const approvalId = typeof trace.review_approval_id === 'string' ? trace.review_approval_id : undefined;
          if (approvalId) {
            map[approvalId] = fields;
          } else {
            unmatchedFields.push(fields);
          }
        }
      }

      // Temporal-style: nested tool_executions inside trace output
      const toolExecs = toRecordArray(trace.output?.tool_executions);
      toolExecs
        .filter((exec) => typeof exec.tool_name === 'string' && exec.tool_name === 'escalate_for_human_review')
        .forEach((exec) => {
          const argsStr =
            typeof exec.args_preview === 'string'
              ? exec.args_preview
              : typeof exec.args === 'string'
                ? exec.args
                : JSON.stringify(exec.args);
          const fields = extractEscalationFields(argsStr);
          if (!fields.reason && !fields.requestedAction) return;

          // Try to extract approval_id from human_response first, then result_preview, then result
          const humanResponse = toRecord(exec.human_response);
          let approvalId: string | undefined;
          if (typeof humanResponse.approval_id === 'string') {
            approvalId = humanResponse.approval_id;
          }
          if (!approvalId) {
            const resultStr =
              typeof exec.result_preview === 'string'
                ? exec.result_preview
                : typeof exec.result === 'string'
                  ? exec.result
                  : undefined;
            const parsedResult = parseJsonIfPossible(resultStr);
            if (
              parsedResult &&
              typeof parsedResult === 'object' &&
              !Array.isArray(parsedResult) &&
              typeof (parsedResult as Record<string, unknown>).approval_id === 'string'
            ) {
              approvalId = (parsedResult as Record<string, unknown>).approval_id as string;
            }
          }

          if (approvalId) {
            map[approvalId] = fields;
          } else {
            unmatchedFields.push(fields);
          }
        });
    });

    // Fallback: match unmatched escalation fields to pending reviews that have no explicit match
    if (unmatchedFields.length > 0 && pendingReviews.length > 0) {
      const unmatchedReviews = pendingReviews.filter((r) => !map[r.approval_id]);
      unmatchedFields.forEach((fields, i) => {
        if (i < unmatchedReviews.length) {
          map[unmatchedReviews[i].approval_id] = fields;
        }
      });
    }

    return map;
  }, [traces, pendingReviews]);

  React.useEffect(() => {
    if (!shouldAutoScrollHistory || !latestStepRef.current) return;
    latestStepRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [displayTraces.length, shouldAutoScrollHistory, showAll, traces.length]);

  if (traces.length === 0) {
    return (
      <div className="text-xs text-zinc-500 py-2 text-center">
        No execution history yet
      </div>
    );
  }

  const formatTimestamp = (timestamp: string | undefined) => {
    if (!timestamp) return 'N/A';
    try {
      return format(new Date(timestamp), 'MMM d, h:mm:ss a');
    } catch {
      return timestamp;
    }
  };

  const historyList = (
    <div className="space-y-1.5">
        {displayTraces.map((trace, index) => {
          const isSuccess = trace.status === 'completed';
          const isFailed = trace.status === 'failed';
          const isRunning = trace.status === 'running';
          const isWaiting = trace.status === 'waiting';
          const isExpanded = expandedStepId === trace.step_id;

          // Extract audio conversation ID from output if available
          const audioConversationId = trace.output
            ?.audio_conversation_id as string | undefined;

          // Get formatted summary of inputs/outputs
          const { inputSummary, outputSummary } = getStepSummary(
            trace.step_type,
            trace.input,
            trace.output ?? undefined,
          );

          // Check if this step has input/output to show
          const hasDetails =
            (trace.input && Object.keys(trace.input).length > 0) ||
            (trace.output && Object.keys(trace.output).length > 0) ||
            trace.error_message;

          return (
            <div
              key={trace.workflow_id || index}
              ref={
                !legacyTemporalStyle && index === displayTraces.length - 1
                  ? latestStepRef
                  : null
              }
              className={cn(
                'rounded border overflow-hidden transition-all',
                isSuccess && 'border-emerald-200 bg-emerald-50/50',
                isFailed && 'border-red-200 bg-red-50/50',
                isRunning && 'border-violet-200 bg-violet-50/50',
                isWaiting && 'border-amber-200 bg-amber-50/50',
                !isSuccess &&
                  !isFailed &&
                  !isRunning &&
                  !isWaiting &&
                  'border-zinc-200 bg-white',
              )}
            >
              {/* Clickable Header */}
              <div
                role="button"
                tabIndex={0}
                className={cn(
                  'flex items-center gap-2 px-2 py-1.5 text-xs cursor-pointer transition-colors',
                  hasDetails && 'hover:bg-black/5',
                )}
                onClick={() =>
                  hasDetails &&
                  setExpandedStepId(isExpanded ? null : trace.step_id)
                }
                onKeyDown={(e) => {
                  if (hasDetails && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    setExpandedStepId(isExpanded ? null : trace.step_id);
                  }
                }}
              >
                {/* Expand/Collapse indicator */}
                {hasDetails ? (
                  <div className="text-zinc-400 shrink-0">
                    {isExpanded ? (
                      <ChevronDown className="h-3 w-3" />
                    ) : (
                      <ChevronRight className="h-3 w-3" />
                    )}
                  </div>
                ) : (
                  <div className="w-3 shrink-0" />
                )}

                {/* Status icon */}
                <div
                  className={cn(
                    'flex items-center justify-center w-5 h-5 rounded-full shrink-0',
                    isSuccess && 'bg-emerald-500 text-white',
                    isFailed && 'bg-red-500 text-white',
                    isRunning && 'bg-violet-500 text-white',
                    isWaiting && 'bg-amber-500 text-white',
                  )}
                >
                  {isSuccess && <Check className="h-3 w-3" />}
                  {isFailed && <X className="h-3 w-3" />}
                  {isRunning && getStepIcon(trace.step_type)}
                  {isWaiting && <Clock className="h-3 w-3" />}
                </div>

                {/* Step info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="font-medium text-zinc-800 truncate">
                      {trace.wait_label || getStepTypeLabel(trace.step_type)}
                    </span>
                    {trace.duration_ms && (
                      <span className="text-zinc-400 text-[10px]">
                        {formatDuration(trace.duration_ms)}
                      </span>
                    )}
                  </div>
                  {/* Input summary (e.g., phone number, email recipient) */}
                  {inputSummary && !isExpanded && (
                    <div className="text-[10px] text-zinc-500 truncate">
                      {inputSummary}
                    </div>
                  )}
                  {/* Output summary (e.g., call outcome, acknowledgement) */}
                  {outputSummary && !isExpanded && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div
                          className={cn(
                            'text-[10px] truncate cursor-help',
                            isSuccess && 'text-emerald-700',
                            isFailed && 'text-red-600',
                            isRunning && 'text-violet-600',
                            !isSuccess &&
                              !isFailed &&
                              !isRunning &&
                              'text-zinc-600',
                          )}
                        >
                          {outputSummary}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-[400px]">
                        <p className="text-xs whitespace-pre-wrap">
                          {outputSummary}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                  {trace.completed_at &&
                    !inputSummary &&
                    !outputSummary &&
                    !isExpanded && (
                      <div className="text-[10px] text-zinc-500">
                        {formatDate(trace.completed_at)}
                      </div>
                    )}
                  {isWaiting && trace.wait_until && !isExpanded && (
                    <div className="text-[10px] text-amber-600">
                      Until {formatDate(trace.wait_until)}
                    </div>
                  )}
                  {isFailed && trace.error_message && !isExpanded && (
                    <div className="text-[10px] text-red-600 truncate">
                      {trace.error_message}
                    </div>
                  )}
                </div>

                {/* Artifact link */}
                {audioConversationId && matterId && (
                  <InspectArtifactButton
                    artifactId={audioConversationId}
                    artifactType={ArtifactType.AUDIO_CONVERSATION}
                    matterId={matterId}
                    compact
                    icon={<Headphones className="h-3 w-3" />}
                  />
                )}

                {/* Action buttons for dialpad/BPO steps */}
                {isDialpadStep(trace.step_type) && (isRunning || isWaiting) && (
                  <div className="flex items-center gap-1 shrink-0">
                    {onMakeCall && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 px-2 text-[10px] text-violet-600 hover:text-violet-700 hover:bg-violet-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          const phone =
                            (trace.input?.adjuster_phone as string) ||
                            (trace.input?.phone_number as string);
                          onMakeCall(phone);
                        }}
                      >
                        <PhoneCall className="h-3 w-3 mr-1" />
                        Call
                      </Button>
                    )}
                    {onEnterResults && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 px-2 text-[10px] text-violet-600 hover:text-violet-700 hover:bg-violet-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEnterResults(trace.step_id);
                        }}
                      >
                        <ClipboardEdit className="h-3 w-3 mr-1" />
                        Results
                      </Button>
                    )}
                  </div>
                )}
              </div>

              {/* Expanded Details */}
              {isExpanded && (
                <div className="px-3 py-2 border-t border-zinc-200 bg-white/50">
                  {isWaiting ? (
                    // Waiting state - show wait info
                    <div className="space-y-2">
                      {trace.wait_until && (
                        <div className="text-[11px]">
                          <span className="font-medium text-zinc-600">Resumes at: </span>
                          <span className="text-amber-600">{formatTimestamp(trace.wait_until)}</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    // Non-waiting state - show custom details view
                      <StepDetailsView
                        trace={trace}
                        runStatus={runStatus}
                        matterId={matterId}
                        disableNestedScroll={useUnifiedScroll}
                        legacyTemporalStyle={legacyTemporalStyle}
                        pendingReviews={pendingReviews}
                        reviewFormDataByApproval={reviewFormDataByApproval}
                        submittingReviewId={submittingReviewId}
                        onReviewFieldChange={onReviewFieldChange}
                        onSubmitReview={onSubmitReview}
                    />
                  )}
                </div>
              )}
            </div>
          );
        })}

        {hasMore && !showAll && (
          <button
            type="button"
            onClick={() => setShowAll(true)}
            className="w-full text-center text-[11px] text-violet-600 hover:text-violet-700 py-1"
          >
            Show {relevantTraces.length - maxItems} earlier steps
          </button>
        )}
      </div>
  );

  return (
    <TooltipProvider delayDuration={200}>
      {showReviewFirst ? (
        <div className="space-y-3">
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
            <div className="text-[11px] font-semibold text-amber-900">Review Required</div>
            <div className="mt-1 text-[11px] text-amber-800">
              The agent is waiting on human review. Submit the review below, or expand the full
              run history if you need the rest of the tool trace.
            </div>
            <div className="mt-3 space-y-2">
              {pendingReviews.map((review) => {
                const fields = escalationFieldsByApprovalId[review.approval_id];
                return (
                  <InlineHumanReviewCard
                    key={review.approval_id}
                    review={review}
                    reviewFormDataByApproval={reviewFormDataByApproval}
                    submittingReviewId={submittingReviewId}
                    legacyTemporalStyle={legacyTemporalStyle}
                    reason={fields?.reason}
                    requestedAction={fields?.requestedAction}
                    onReviewFieldChange={onReviewFieldChange}
                    onSubmitReview={onSubmitReview}
                  />
                );
              })}
            </div>
          </div>
          <details
            className="rounded-lg border border-zinc-200 bg-white"
            open={showReviewHistory}
            onToggle={(event) => {
              setShowReviewHistory(event.currentTarget.open);
            }}
          >
            <summary className="cursor-pointer list-none px-3 py-2 text-[11px] font-medium text-zinc-700">
              Show full run details
            </summary>
            <div className="border-t border-zinc-200 px-2 py-2">
              {historyList}
            </div>
          </details>
        </div>
      ) : (
        historyList
      )}
    </TooltipProvider>
  );
};

export default CrashingOutExecutionHistory;
